import React from 'react';
import Box from '@mui/material/Box';

import { color, r } from '../../theme/tokens';
import {
  MERGED_AMOUNTS_NOTE,
  NOT_IN_LIBRARY_NOTE,
  NO_STUDIES_NOTE,
  withSupplementNames,
} from './copy';

/**
 * AnalysisNotes — the muted slot under an analysed supplement list.
 *
 * It renders the second and third lines of STACK-544's ruled order. The first
 * is the existing interaction line (`SafetyNote`), which this ticket does not
 * change and which stays where it already is; this component is mounted
 * directly after it so they read as one block. A third line follows
 * the two ruled ones: supplements the analysis found no studies on for this
 * goal (Tanu, 2026-09-25).
 *
 * **A clean result renders nothing at all** — not an empty container, not a
 * spacer. Each line is conditional on its own flag, and with all of them false the
 * component returns null so the list's bottom margin is the same as on any
 * other stack.
 *
 * Neither line is advice. They explain what the analysis did and did not
 * cover, which is the member's to know; what to do about it is out of scope
 * (PRD). Each line names the supplements it applies to (Tanu, 2026-09-24), so
 * the member does not have to work out which rows it means.
 */
const AnalysisNotes = ({
  hasMerged = false,
  hasNotInLibrary = false,
  hasNoStudies = false,
  mergedNames = [],
  notInLibraryNames = [],
  noStudiesNames = [],
}) => {
  if (!hasMerged && !hasNotInLibrary && !hasNoStudies) return null;

  return (
    <Box
      data-testid="analysis-notes"
      sx={{
        marginTop: r(10),
        display: 'flex',
        flexDirection: 'column',
        gap: r(4),
        // Same type as the interaction line above (`SafetyNote`), so the
        // three read as one block (Tanu, 2026-09-24).
        fontSize: r(11.5),
        lineHeight: 1.55,
        color: color.inkMuted,
      }}
    >
      {hasMerged && (
        <Box component="p" sx={{ margin: 0 }}>
          {withSupplementNames(MERGED_AMOUNTS_NOTE, mergedNames)}
        </Box>
      )}
      {hasNotInLibrary && (
        <Box component="p" sx={{ margin: 0 }}>
          {withSupplementNames(NOT_IN_LIBRARY_NOTE, notInLibraryNames)}
        </Box>
      )}
      {hasNoStudies && (
        <Box component="p" sx={{ margin: 0 }}>
          {withSupplementNames(NO_STUDIES_NOTE, noStudiesNames)}
        </Box>
      )}
    </Box>
  );
};

export default AnalysisNotes;
